import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Panel } from '@vp/component-library';
import { useTranslations } from '@vp/om-translations';

import { SelectionSet, Typography, Link, Icon } from '@vp/swan';
import {
  shippingOptionSelectors,
  shippingOptionOperations,
} from '../../state/shippingOptions';
import { ShippingQuote } from './ShippingQuote';
import useSiteData from '../../hooks/useSiteData';
import type { RootState, ShippingOption } from '../../types';
import useTracking from '../../hooks/useTracking';
import { SHIPPING_IN_CART } from '../../optimizely/optimizelyExperiments';

export const ShippingOptionsPanel = ({
  setIsOpen,
}: {
  setIsOpen: Function;
}) => {
  const dispatch = useDispatch();
  const tracker = useTracking();
  const { externalAppNavigation } = useSiteData();
  const [shippingMessages] = useTranslations(['shippingMessages']);

  const shippingOptions = useSelector((state: RootState) =>
    shippingOptionSelectors.selectShippingOptionsToDisplay(
      state.shippingOptions
    )
  );

  const selectedShippingMethodId = useSelector((state: RootState) =>
    shippingOptionSelectors.selectSelectedShippingMethodId(
      state.shippingOptions
    )
  );

  const onSaveHandler = useCallback(
    (shippingMethodId: string) => {
      dispatch(
        shippingOptionOperations.saveSelectedShippingOption(
          shippingMethodId
        ) as any
      );
      tracker.trackExperimentClicked(
        externalAppNavigation.cart,
        externalAppNavigation.cart,
        'Save Shipping Method',
        SHIPPING_IN_CART.experimentKey,
        SHIPPING_IN_CART.activeVariant,
        ''
      );
      setIsOpen(false);
    },
    // Disable this rule because 'tracker' instance changes each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, externalAppNavigation.cart, setIsOpen]
  );

  return (
    <Panel id="choose-shipping-option-panel" title="Choose shipping method">
      <SelectionSet
        variant="single-select"
        onSelectedValueChange={id => {
          onSaveHandler(id as string);
        }}
        selectedValue={selectedShippingMethodId}
      >
        {shippingOptions.map((shippingOption: ShippingOption) => (
          <ShippingQuote
            shippingOption={shippingOption}
            key={`${shippingOption.shippingMethodId}-shipping-quote`}
          />
        ))}
      </SelectionSet>

      {externalAppNavigation.delivery ? (
        <Link href={externalAppNavigation.delivery} target="_blank">
          <Icon iconType="info" size="16p" mr={1} />
          <Typography fontSize={-1} as="span">
            {shippingMessages.deliveryDetailsMessage}
          </Typography>
        </Link>
      ) : null}
    </Panel>
  );
};
